import { useState, useEffect, useMemo } from 'react';
import { GripVertical } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useRaces } from '@/hooks/useRaces';
import { useRaceTypes } from '@/hooks/useRaceTypes';
import { useTshirtSizes } from '@/hooks/useTshirtSizes';
import { ActionButtons, editAction, deleteAction } from '@/components/shared';
import { useTshirtSizesActions } from './useTshirtSizesActions';
import TshirtSizesManagerView from './tshirtsizesmanager.view';

interface TshirtSizesManagerOwnProps {
  raceId?: string;
}

const TshirtSizesManager: React.FC<TshirtSizesManagerOwnProps> = ({ raceId: raceIdProp }) => {
  const { user, isOrganizer } = useAuth();

  const isEmbedded = !!raceIdProp;

  const raceParams = useMemo(() => {
    const params: Record<string, any> = {};
    if (isOrganizer() && user?.organizerId) {
      params.organizerId = user.organizerId;
    }
    return params;
  }, [user, isOrganizer]);

  const { data: races = [] } = useRaces(raceParams);
  const [selectedRaceIdState, setSelectedRaceIdState] = useState('');
  const selectedRaceId = isEmbedded ? raceIdProp : selectedRaceIdState;
  const [selectedRaceTypeId, setSelectedRaceTypeId] = useState('');

  useEffect(() => {
    if (!isEmbedded && races.length > 0 && !selectedRaceIdState) {
      setSelectedRaceIdState(races[0].id);
    }
  }, [races, selectedRaceIdState, isEmbedded]);

  const { data: raceTypes = [] } = useRaceTypes(selectedRaceId);
  const { data: sizes = [], isLoading: loading } = useTshirtSizes(
    selectedRaceId,
    selectedRaceTypeId || undefined
  );

  const actions = useTshirtSizesActions(selectedRaceId, selectedRaceTypeId);

  const sizeColumns = useMemo(() => [
    { key: '_index', label: '#', cellClassName: 'whitespace-nowrap text-gray-500 text-sm', render: (_: any, __: any, idx: number) => (
      <><GripVertical className="w-4 h-4 inline mr-1 text-gray-300" />{idx + 1}</>
    )},
    { key: 'size', label: 'Големина', cellClassName: 'whitespace-nowrap', render: (v: any) => <span className="font-semibold text-gray-900 bg-gray-100 px-3 py-1 rounded">{v}</span> },
    { key: 'sortOrder', label: 'Редослед', cellClassName: 'whitespace-nowrap text-gray-600' },
    { key: 'raceTypeId', label: 'Тип на трка', cellClassName: 'whitespace-nowrap text-gray-600', render: (v: any) => {
      if (!v) return <span className="text-gray-400 italic">Сите</span>;
      return raceTypes.find((rt: any) => rt.id === v)?.name || `ID: ${v}`;
    }},
    { key: '_actions', label: 'Акции', className: 'text-right', cellClassName: 'whitespace-nowrap text-right', render: (_: any, size: any) => (
      <ActionButtons actions={[editAction(() => actions.handleEdit(size)), deleteAction(() => actions.handleDelete(size.id))]} />
    )},
  ], [raceTypes, actions]);

  return (
    <TshirtSizesManagerView
      races={isEmbedded ? [] : races}
      selectedRaceId={selectedRaceId}
      setSelectedRaceId={setSelectedRaceIdState}
      selectedRaceTypeId={selectedRaceTypeId}
      setSelectedRaceTypeId={setSelectedRaceTypeId}
      raceTypes={raceTypes}
      sizes={sizes}
      loading={loading}
      sizeColumns={sizeColumns}
      isDialogOpen={actions.isDialogOpen}
      setIsDialogOpen={actions.setIsDialogOpen}
      editingSize={actions.editingSize}
      setEditingSize={actions.setEditingSize}
      formData={actions.formData}
      setFormData={actions.setFormData}
      handleSubmit={actions.handleSubmit}
      deleteTarget={actions.deleteTarget}
      setDeleteTarget={actions.setDeleteTarget}
      confirmDelete={actions.confirmDelete}
      addDefaultSizes={actions.addDefaultSizes}
      embedded={isEmbedded}
    />
  );
};

export default TshirtSizesManager;
